import { AddRounded, ArrowForwardIosRounded, CancelRounded, CheckCircleRounded, DoNotDisturbRounded, FiberNewRounded, FilterListRounded, MopedRounded, NavigateNextRounded, PersonRounded, PersonSearchRounded, RunCircleRounded } from "@mui/icons-material";
import { Badge, Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, ListItemIcon, Menu, MenuItem, Paper, Select, SelectChangeEvent, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";
import { Query, QueryDocumentSnapshot, collection, endBefore, getCountFromServer, getDocs, getFirestore, limit, limitToLast, orderBy, query, startAfter, startAt, where } from "firebase/firestore";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useAppDispatch, useAppSelector } from "../../store";
import { setVacationPerPage } from "../../store/slices/appSlice";
import { RequestStatus } from "../../types/Type";
import { Vacation } from "../../types/Vacation";
import { queryVacations } from "../../utils/queryVacations";
import { updateVacationRequestStatus } from "../../utils/updateVacation";
import { ChipIcon, ChipColor } from '../ChipUtils';
import { VacationRequestInfoBox } from "./VacationRequestInfoBox";
import { Search, SearchIconWrapper, StyledInputBase } from "../Search";
import uniformRequestConverter from "../../converters/uniformRequestConverter";
import vacationRequestConverter from "../../converters/vacationRequestConverter";
import { VacationRequestForm } from "./VacationRequestForm";
import { Employee } from "../../types/Employee";
import { createVacationRequest } from "../../utils/createVacationRequest";
import { hasPermission } from "../../security/Permission";

const getBadgeIconFromStatus = (status: RequestStatus | 'All') => {
  if (status === 'All') return undefined;
  else if (status === 'Processing') return <RunCircleRounded color='warning' />;
  else if (status === 'Approved') return <CheckCircleRounded color='success' />;
  else if (status === 'Denied') return <DoNotDisturbRounded color='error' />;
  else if (status === 'Cancelled') return <CancelRounded color='error' />;
  return <FiberNewRounded color='info' />;
};


interface VacationRequestsPageProps {

};
export const VacationRequestsPage = (props: VacationRequestsPageProps) => {
  const db = getFirestore();
  const { enqueueSnackbar  } = useSnackbar();
  const dispatch = useAppDispatch();
  const rowsPerPage = useAppSelector((s) => s.app.vacationPerPage);
  const authState = useAppSelector(s => s.auth);

  const navigate = useNavigate();
  
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const [firstVacation, setFirstVacation] = useState<QueryDocumentSnapshot | null>(null);
  const [lastVacation, setLastVacation] = useState<QueryDocumentSnapshot | null>(null);
  const [vacationRequests, setVacationRequests] = useState<Vacation[]>([]);
  
  const [selected, setSelected] = useState<Vacation>();
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState<RequestStatus | 'All'>('New');
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);

  const [employee, setEmployee] = useState<Employee | undefined>();
  const [showVacationRequestForm, setShowVacationRequestForm] = useState(false);
  const [startDate, setStartDate] = useState(DateTime.now().toFormat('yyyyMMdd'));
  const [endDate, setEndDate] = useState(DateTime.now().toFormat('yyyyMMdd'));
  const [disableSaveButton, setDisableSaveButton] = useState(false);

  const filterAll = () => setFilter('All');
  const filterNew = () => setFilter('New');
  const filterProcessing = () => setFilter('Processing');
  const filterApproved = () => setFilter('Approved');
  const filterDenied = () => setFilter('Denied');
  const filterCancelled = () => setFilter('Cancelled');

  const refresh = (callback?: (q: Query<Vacation>) => Query<Vacation>) => {
    let q: Query<Vacation> = query(collection(db, 'Vacations'), orderBy('idNumber', 'asc'), orderBy('dateRequested', 'desc')).withConverter(vacationRequestConverter);
    
    if(callback)
      q = callback(q);
    if (filter !== 'All')
      q = query(q, where('requestStatus', '==', filter));
    getDocs(q).then(qSnapshot => {
      setVacationRequests(qSnapshot.docs.map(d => d.data()));
      setFirstVacation(qSnapshot.docs[0]);
      setLastVacation(qSnapshot.docs[qSnapshot.docs.length - 1]);
    });

    // Count query
    let qCount: Query = query(collection(db, `Vacations`));
    if (filter !== 'All') 
      qCount = query(qCount, where('requestStatus', '==', filter));
    getCountFromServer(qCount).then(f => setTotalRows(f.data().count));
  };
  
  const handleShowVacationRequestForm = () => setShowVacationRequestForm(true);
  const handleCancelVacationRequestForm = () => {
    setStartDate(DateTime.now().toFormat('yyyyMMdd'));
    setEndDate(DateTime.now().toFormat('yyyyMMdd'));
    setShowVacationRequestForm(false);
  };
  
  const handleVacationRequestFormOnChange = (emp: Employee, s: string, e: string) => {
    setEmployee(emp)
    setStartDate(s);
    setEndDate(e);
  };

  const handleSaveVacationRequest = () => {
    if  (employee) {
      if (window.confirm('Are you sure you want to save the vacation request?')) {
        setDisableSaveButton(true);
        createVacationRequest(employee, startDate, endDate).then(r => {
          if (r) {
            refresh();
            enqueueSnackbar('Vacation request has been created!', { variant: 'success' });
          }
          setEmployee(undefined);
          setStartDate(DateTime.now().toFormat('yyyyMMdd'));
          setEndDate(DateTime.now().toFormat('yyyyMMdd'));
          setShowVacationRequestForm(false);
          setDisableSaveButton(false);
        });
      }
    }
  };

  const updateVacationStatus = (r: RequestStatus) => {
    if (selected) {
      updateVacationRequestStatus(selected, r).then(() => {
        const updatedValue: Vacation = { ...selected, requestStatus: r };

        // Update selected and list
        const copyA = [...vacationRequests];

        const idxA = copyA.findIndex((e) => e.id === selected.id);

        if (idxA !== -1) copyA[idxA] = updatedValue;

        setVacationRequests(copyA);
        setSelected(updatedValue);

        if (filter !== 'All' && filter !== r) {
          // Remove from filter
          setTimeout(() => {
            copyA.splice(idxA, 1);
            
            setVacationRequests([...copyA]);
          }, 1000);
        }

        enqueueSnackbar(
          r === 'Approved' 
            ? 'Vacation request has been approved.'
          : r === 'Denied'
            ? 'Vacation request has been denied.'
            : 'Vacation request status has been updated.', 
          { variant: 'success' });
      });
    }
  };

  const searchDebouncedCallback = useDebouncedCallback((value) => {
    refresh(q => query(q, where('idNumber', '>=', value), where('idNumber', '<=', value + '\uf8ff'), limit(rowsPerPage)));
  }, 1000);

  const handleSearchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    searchDebouncedCallback(event.currentTarget.value);
  };

  const handleFilterButtonOnClick = (event: React.MouseEvent<HTMLElement>) => setFilterAnchorEl(event.currentTarget);
  const handleFilterMenuOnClose = () => setFilterAnchorEl(null);

  const handlePageOnChange = (event: React.MouseEvent<HTMLButtonElement> | null, to: number) => {
    if (to > page) {
      // Next
      refresh(q => {
        return query(q, startAfter(lastVacation), limit(rowsPerPage));
      });
    } else if (to < page) {
      // Previous
      refresh(q => {
        return query(q, endBefore(firstVacation), limitToLast(rowsPerPage));
      });
    } else {
      // Same
      refresh(q => {
        return query(q, startAt(firstVacation), limit(rowsPerPage));
      });
    }
    setPage(to);
  };

  const handleRowsPerPageOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setVacationPerPage(+event.target.value));
    setPage(0);
  };

  const handleDenyRequest = () => updateVacationStatus('Denied');
  const handleApproveRequest = () => updateVacationStatus('Approved');

  useEffect(() => {
    refresh(q => query(q, limit(rowsPerPage)));
    setFilterAnchorEl(null);
  }, [rowsPerPage, filter]);

  return (
    <>
      <Container disableGutters>
        <Typography variant="h5" mt={2} mb={5}>Vacation Requests</Typography>
        <Stack direction='row' mb={1}>
          <Box width={650}>
            <Search>
              <SearchIconWrapper>
                <PersonSearchRounded color='primary' />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search employee by Id Number"
                inputProps={{ 'aria-label': 'search' }}
                onChange={handleSearchOnChanged}
              />
            </Search>
          </Box>
          <Box flexGrow={1} />
          {hasPermission(authState, [], ['create.vacations']) && (
            <Button startIcon={<AddRounded />} onClick={handleShowVacationRequestForm}>Create new request</Button>
          )}
        </Stack>
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={76} align="right">Count</TableCell>
                  <TableCell width={120}>Id Number</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell width={220}>Date Requested</TableCell>
                  <TableCell width={200}>
                    <Stack 
                      direction='row' 
                      alignItems='center'
                      sx={{
                        backgroundColor: '#0000001f',
                        borderRadius: 2,
                        borderWidth: 1,
                        borderColor: '#0000004f',
                        borderStyle: 'solid',
                        padding: 1,
                      }}
                    >
                      <Box flexGrow={1}>
                        Request Status
                      </Box>
                      <Tooltip title='Filter Request Status'>
                        <IconButton size='small' onClick={handleFilterButtonOnClick}>
                          <Badge badgeContent={filter !== 'All' ? getBadgeIconFromStatus(filter) : 0}>
                            <FilterListRounded />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell width={40} />
                </TableRow>
              </TableHead>
              <TableBody>
                {vacationRequests.map((v, i) => {
                  return (
                    <TableRow 
                      hover
                      key={v.id}
                      sx={{
                        cursor: 'pointer',
                        '&:last-child td, &:last-child th': { border: 0 } 
                      }}
                      onClick={hasPermission(authState, [], ['update.vacations']) ? () => setSelected(v) : undefined}
                    >
                      <TableCell align="right">{i + 1}</TableCell>
                      <TableCell>{v.idNumber}</TableCell>
                      <TableCell>{v.name}</TableCell>
                      <TableCell>{v.dateRequested ? DateTime.fromISO(v.dateRequested).toLocaleString(DateTime.DATETIME_MED) : ''}</TableCell>
                      <TableCell>
                        <Chip icon={ChipIcon(v.requestStatus!)} label={v.requestStatus} color={ChipColor(v.requestStatus!)} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <IconButton color="info"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/services/vacationRequests/${v.id}`, {
                              state: {
                                vacation: v,
                              }
                            })
                          }}
                        >
                          <ArrowForwardIosRounded />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[25, 50, 100]}
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage ?? 25}
            SelectProps={{
              inputProps: {
                'aria-label': 'rows per page',
              },
            }}
            onPageChange={handlePageOnChange}
            onRowsPerPageChange={handleRowsPerPageOnChange}
            ActionsComponent={TablePaginationActions} />
        </Paper>
      </Container>
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterMenuOnClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 12,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={filterAll}>
          <ListItemIcon></ListItemIcon>
          All
        </MenuItem>
        <MenuItem onClick={filterNew}>
          <ListItemIcon>
            <FiberNewRounded color='info' />
          </ListItemIcon>
          New
        </MenuItem>
        <MenuItem onClick={filterProcessing}>
          <ListItemIcon>
            <RunCircleRounded color='warning' />
          </ListItemIcon>
          Processing
        </MenuItem>
        <MenuItem onClick={filterApproved}>
          <ListItemIcon>
            <CheckCircleRounded color='success' />
          </ListItemIcon>
          Approved
        </MenuItem>
        <MenuItem onClick={filterDenied}>
          <ListItemIcon>
            <DoNotDisturbRounded color='error' />
          </ListItemIcon>
          Denied
        </MenuItem>
        <MenuItem onClick={filterCancelled}>
          <ListItemIcon>
            <CancelRounded color='error' />
          </ListItemIcon>
          Cancelled
        </MenuItem>
      </Menu>
      {hasPermission(authState, [], ['update.vacations']) && (
        <Dialog open={selected !== undefined} onClose={() => setSelected(undefined)} fullWidth>
          <DialogTitle><Chip icon={<PersonRounded />} label={selected?.idNumber} sx={{ mr: 2 }} />Vacation Request</DialogTitle>
          <DialogContent dividers>
            <VacationRequestInfoBox vacation={selected} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelected(undefined)}>Close</Button>
            {selected?.requestStatus == 'New' && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <Button color="error" startIcon={<DoNotDisturbRounded />} onClick={handleDenyRequest}>Deny</Button>
                <Button color="success" startIcon={<CheckCircleRounded />} onClick={handleApproveRequest}>Approve</Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )}
      {hasPermission(authState, [], ['create.vacations']) && (
        <Dialog open={showVacationRequestForm} onClose={() => setShowVacationRequestForm(false)} fullWidth>
          <DialogTitle>Create Vacation Request</DialogTitle>
          <DialogContent dividers>
            <VacationRequestForm onChanged={handleVacationRequestFormOnChange} />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={handleCancelVacationRequestForm}>CANCEL</Button>
            <Button color="primary" onClick={handleSaveVacationRequest} disabled={disableSaveButton}>SAVE</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
};